import { Reveal, Eyebrow, H2, Section } from "./ui";
import { User, Users, Send, Check, X } from "lucide-react";

const SCENES = [
  "Eres quien sabe cuándo toca la vacuna, qué talla usa y qué falta en la nevera.",
  "3:12 de la madrugada. Otra vez te levantas tú.",
  "Pides ayuda y te contestan: \"Haberlo dicho\".",
  "La cena acaba con gritos… y no solo los de tu hijo/a.",
  "En plena rabieta, tu pareja cede y te toca a ti sostener el límite… otra vez.",
  "Os vais a la cama sin hablaros, con el móvil en la mano y un nudo en el pecho.",
];

export const TeSuena = () => (
  <Section id="te-suena" testid="section-te-suena" className="bg-[var(--cream-2)]">
    <Reveal><Eyebrow>¿Te suena?</Eyebrow><H2 className="max-w-2xl">Si te has visto en más de una, no te pasa solo a ti. Y no es culpa tuya.</H2></Reveal>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {SCENES.map((s, i) => (
        <Reveal key={i} delay={i * 0.04}>
          <div className={`h-full rounded-3xl p-6 bg-white border-2 border-[var(--ink)] ${i % 2 ? "rotate-[0.8deg]" : "-rotate-[0.8deg]"}`} data-testid={`scene-card-${i}`}>
            <p className="font-display text-4xl text-[var(--terra)] leading-none">“</p>
            <p className="text-lg mt-1">{s}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

const PROBLEM_IMG = "https://images.unsplash.com/photo-1769720199212-a15310fdb5c2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000";

export const Problema = () => (
  <Section id="problema" testid="section-problema">
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <Reveal><img src={PROBLEM_IMG} alt="Niña pequeña con harina en la cara" loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover rounded-[2rem]" /></Reveal>
      <Reveal delay={0.1}>
        <Eyebrow color="var(--sage-deep)">El problema real</Eyebrow>
        <H2>No es tu hijo/a. No es tu pareja. Y desde luego, no eres tú.</H2>
        <p className="mt-6 text-lg text-[#5a4f46]">Es el <b className="text-[var(--ink)]">agotamiento sin un plan común</b>. Cuando duermes poco, cargas con la agenda mental de toda la familia y la preocupación por tu hijo/a lo ocupa todo, la pareja se convierte en el sitio donde estalla la tensión.</p>
        <p className="mt-4 text-lg text-[#5a4f46]">La buena noticia: no necesitas más horas ni hacerlo perfecto. Necesitas <b className="text-[var(--ink)]">pequeñas herramientas que se repiten cada día</b> y a alguien que te acompañe mientras las pones en práctica.</p>
      </Reveal>
    </div>
  </Section>
);

const EDITORIAL_AREAS = [
  ["Relaciones y pareja", "Comunicación, acuerdos y conexión en la vida cotidiana."],
  ["Crianza y desarrollo", "Comprender las necesidades infantiles y acompañar cada etapa."],
  ["Bienestar emocional", "Poner palabras a lo que sentimos y buscar apoyos útiles."],
  ["Vida familiar", "Organización, cuidados y decisiones compartidas en casa."],
];

export const EnfoqueEditorial = () => (
  <Section id="enfoque-editorial" testid="editorial-brand-section" className="bg-[var(--cream-2)]">
    <div className="max-w-4xl">
      <Eyebrow>Una mirada editorial y práctica</Eyebrow>
      <H2>Ideas para entender lo que pasa y probar pequeños cambios en la vida real.</H2>
      <p className="mt-5 max-w-3xl text-lg text-[#5a4f46]">En Dos Mundos acerca la psicología a las relaciones, la familia y la crianza con contenidos psicoeducativos claros, reflexivos y útiles para el día a día.</p>
    </div>
    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-7">
      {EDITORIAL_AREAS.map(([title, description], index) => (
        <article key={title} className="border-t-2 border-[var(--terra)] py-4" data-testid={`editorial-area-${index}`}>
          <p className="text-xs font-semibold uppercase text-[var(--sage-deep)]">0{index + 1}</p>
          <h3 className="font-display text-xl mt-2">{title}</h3>
          <p className="mt-2 text-sm text-[#5a4f46]">{description}</p>
        </article>
      ))}
    </div>
  </Section>
);

export const QuienesSomos = () => (
  <Section id="quienes-somos" testid="about-team-section">
    <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14 items-center">
      <Reveal>
        <figure>
          <img src="/equipo-en-calma-conversacion.png" alt="Ilustración editorial de una pareja conversando y organizando un acuerdo familiar" loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover rounded-2xl" />
          <figcaption className="mt-2 text-xs text-[#7a6c5e]">Una marca creada para pensar en las relaciones, los cuidados y la vida familiar.</figcaption>
        </figure>
      </Reveal>
      <Reveal delay={0.08}>
        <Eyebrow>Quiénes somos</Eyebrow>
        <H2>Psicoeducación para la vida familiar.</H2>
        <div className="mt-5">
          <p className="text-xs font-semibold uppercase text-[var(--sage-deep)]">Creadora y titular del proyecto</p>
          <h3 className="font-display text-2xl mt-2">Sara Álvarez</h3>
          <p className="mt-2 text-[#5a4f46]">Graduada en Psicología, colegiada 26982, con formación psicoeducativa en desarrollo infantil.</p>
        </div>
        <div className="mt-7 border-t border-[#d9cbb8] pt-5">
          <p className="text-xs font-semibold uppercase text-[var(--sage-deep)]">Colaboradora profesional sanitaria</p>
          <h3 className="font-display text-2xl mt-2">Cristina Artamendi</h3>
          <p className="mt-2 text-[#5a4f46]">Psicóloga General Sanitaria, Máster en Psicología General Sanitaria, colegiada 28806.</p>
        </div>
      </Reveal>
    </div>
  </Section>
);

const GAINS = [
  ["Cansancio y sensación de soledad.", "Ejercicios para abrir conversaciones y pedir apoyo."],
  ["Desacuerdos en las rabietas.", "Prácticas para acordar cómo responder en familia."],
  ["La carga mental parece recaer en una persona.", "Un planificador para revisar y repartir tareas."],
  ["Las discusiones suben de tono.", "Pautas para hacer una pausa y retomar la conversación."],
  ["Cuesta encontrar tiempo de pareja.", "Ideas breves para reservar momentos de conexión."],
];

export const Conseguiras = () => (
  <Section id="conseguiras" testid="section-conseguiras" className="bg-[var(--sage-deep)] text-[var(--cream)]">
    <Reveal><Eyebrow color="var(--butter)">Qué practicaréis</Eyebrow><H2 className="max-w-2xl">Herramientas para afrontar la crianza en equipo.</H2></Reveal>
    <div className="mt-12 space-y-4">
      <div className="hidden sm:grid grid-cols-2 gap-6 text-xs uppercase text-white/60"><span>Situaciones habituales</span><span>Prácticas del programa</span></div>
      {GAINS.map(([before, after], i) => (
        <Reveal key={before} delay={i * 0.07}>
          <div className="grid sm:grid-cols-2 gap-3 sm:gap-6 border-t border-white/20 pt-5" data-testid={`transformation-row-${i}`}>
            <p className="text-white/65 line-through decoration-[var(--terra)] decoration-2">{before}</p>
            <p className="text-base sm:text-lg font-semibold flex gap-3"><span className="text-[var(--butter)]" aria-hidden="true">→</span>{after}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

const WAYS = [
  [User, "En solitario", "Puedes hacer el recorrido por tu cuenta y probar las herramientas en tu día a día."],
  [Users, "En pareja", "Lo hacéis los dos a la vez: cada día incluye una pregunta para hablarlo juntos."],
  [Send, "Invitando después", "Empiezas tú y, cuando notes los cambios, se lo enseñas. Sin presiones."],
];

export const Formas = () => (
  <Section id="formas" testid="section-formas">
    <Reveal><Eyebrow>Un formato flexible</Eyebrow><H2 className="max-w-2xl">Podéis empezar juntos o empezar una persona.</H2></Reveal>
    <div className="mt-12 grid md:grid-cols-3 gap-5">
      {WAYS.map(([Icon, t, d], i) => (
        <Reveal key={t} delay={i * 0.08}>
          <div className="h-full rounded-3xl p-7 bg-white shadow-[0_10px_30px_-15px_rgba(42,37,33,0.3)] hover:-translate-y-1 transition-transform" data-testid={`way-card-${i}`}>
            <div className="w-12 h-12 rounded-2xl grid place-items-center bg-[var(--butter)]"><Icon size={22} /></div>
            <p className="font-display text-2xl mt-5">{t}</p>
            <p className="mt-2 text-[#5a4f46]">{d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

const YES = ["Tienes un hijo o hija de 0 a 5 años.", "Te agota pensarlo todo y ser quien siempre tira del carro.", "Sientes que en tu relación de pareja ya solo hay logística y cansancio.", "Discutís por las rabietas, el sueño o el reparto.", "Quieres algo práctico, breve y a tu ritmo, y que alguien te acompañe."];
const NO = ["Buscas una solución mágica sin dedicarle 10 minutos.", "Hay violencia o maltrato en la relación (aquí necesitas ayuda especializada).", "Buscas que alguien trate directamente a tu hijo/a."];

export const EsParaTi = () => (
  <Section id="es-para-ti" testid="section-es-para-ti" className="bg-[var(--cream-2)]">
    <Reveal><Eyebrow>Filtro honesto</Eyebrow><H2>¿Es para ti?</H2></Reveal>
    <div className="mt-12 grid md:grid-cols-2 gap-5">
      <Reveal><div className="rounded-3xl p-7 bg-white h-full" data-testid="for-you-list">
        <p className="font-semibold text-lg mb-4">Es para ti si…</p>
        {YES.map((t) => <p key={t} className="flex gap-3 py-2"><Check className="shrink-0 text-[var(--sage-deep)]" />{t}</p>)}
      </div></Reveal>
      <Reveal delay={0.1}><div className="rounded-3xl p-7 border-2 border-dashed border-[#b9a894] h-full" data-testid="not-for-you-list">
        <p className="font-semibold text-lg mb-4">No es para ti si…</p>
        {NO.map((t) => <p key={t} className="flex gap-3 py-2 text-[#5a4f46]"><X className="shrink-0 text-[var(--terra)]" />{t}</p>)}
      </div></Reveal>
    </div>
  </Section>
);
